/**
 * Provides font components.
 */
import * as React from 'react';

import { TextProps, Platform } from 'react-native';
import styled from 'styled-components/native';
import { color, ColorProps, typography, TypographyProps } from 'styled-system';

import { Colors } from 'theme/colors';
import { FontFamily, FontSize, FontWeight } from 'theme/fonts';
import { useViewType, ViewType } from 'hooks/screen';

const DEFAULT_COLOR = Colors.black;
const BACK_BUTTON_SIZE = 56;
const PLATFORM_FONT_FAMILY = FontFamily.Regular;
const DEFAULT_LINE_HEIGHT = 1.47;
const FONT_SCALING_FACTOR = 1.0625;

export const BaseRegular = styled.Text<ColorProps & TypographyProps>`
  color: ${DEFAULT_COLOR};
  font-family: ${PLATFORM_FONT_FAMILY};
  font-size: ${FontSize.Regular};
  font-weight: ${FontWeight.Regular};
  text-align: left;
  line-height: ${({ fontSize }) => DEFAULT_LINE_HEIGHT * parseInt(fontSize as string)}px;
  ${color}
  ${typography}
`;

/**
 * Scalable Font
 *
 * @param props - Render props
 */
export function Regular(props: TextProps & FontProps) {
  const viewType = useViewType();
  const { fontSize = FontSize.Regular, fontFamily, ...otherProps } = props;
  let adjustedFontSize = fontSize;
  const adjustedFontFamily = Platform.OS === 'web' ? ('Roboto' as FontFamily) : fontFamily;

  if (viewType !== ViewType.MOBILE) {
    const scaledSize = parseInt(fontSize as string) * FONT_SCALING_FACTOR;
    const scaledPixelSize = Math.round(scaledSize);

    adjustedFontSize = `${scaledPixelSize}px` as FontSize;
  }

  return (
    <BaseRegular
      fontFamily={adjustedFontFamily}
      color={Colors.charcoal}
      fontSize={adjustedFontSize}
      {...otherProps}
    />
  );
}
export const Bold = styled(Regular).attrs({ fontFamily: FontFamily.Bold as FontFamily })`
  font-weight: ${FontWeight.Bold};
`;

export const Light = styled(Regular).attrs({ fontFamily: FontFamily.Light as FontFamily })`
  font-weight: ${FontWeight.Light};
`;

export const Medium = styled(Regular).attrs({ fontFamily: FontFamily.Medium as FontFamily })`
  font-weight: ${FontWeight.Medium};
`;

export const Thin = styled(Regular).attrs({ fontFamily: FontFamily.Thin as FontFamily })`
  font-weight: ${FontWeight.Thin};
`;

export const Title = styled(Regular)`
  color: ${({ color }) => color || Colors.snow};
  font-size: ${({ fontSize }) => fontSize || FontSize.Regular};
  font-family: ${({ fontFamily }) => fontFamily || FontFamily.Medium};
`;

export const ErrorText = styled(Light)`
  font-size: ${FontSize.Medium};
  include-font-padding: false;
  line-height: 24px;
  margin: 0;
  margin-right: 40;
  text-align: center;
  text-align-vertical: center;
`;
/**
 * Text Component to be used for Submit Buttons.
 *
 * @param props - Font props.
 */
export const SubmitText = styled(Medium)`
  color: ${Colors.snow};
  font-size: ${FontSize.Regular};
  text-align: center;
`;

/**
 * Regular Text for Headers.
 *
 * @param props - Render props.
 */
export function HeaderTitle(props: HeaderTitleProps) {
  const { hasBackButton, textAlign, children } = props;
  const style = { flex: 1, marginLeft: hasBackButton ? -BACK_BUTTON_SIZE : 0 };

  return (
    <Regular textAlign={textAlign || 'center'} style={style}>
      {children}
    </Regular>
  );
}
interface HeaderTitleProps {
  /** If true, will adjust for back button */
  hasBackButton: boolean;
  /** Alignment of the header text */
  textAlign: AlignSetting;
  /** Any components to display inside text node */
  children?: React.ReactNode;
}
export interface FontProps {
  children?: React.ReactNode;
  /** Color of the text */
  color?: string;
  /** Font family of the text */
  fontFamily?: FontFamily;
  /** Size of the text */
  fontSize?: FontSize | number;
  /** A weighting to apply */
  fontWeight?: FontWeight;
  /** Alignment of the text */
  textAlign?: AlignSetting;
  /** Line height of the text */
  lineHeight?: number;
}
